import { Group, Text, Button, Box, Grid, ActionIcon, Flex, Container, Card, Switch, Skeleton, Input } from '@mantine/core';
import { IconTrash, IconPencil, IconPlayerPlay, IconPlayerPause, IconCaretUpFilled, IconCaretDownFilled, IconX, IconSearch } from '@tabler/icons-react';
import { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import { useDocumentTitle } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { HeaderMenu } from "../../components/HeaderMenu/HeaderMenu";
import { useNavigate } from "react-router-dom";
import InsightsApi from "../../api/InsightsApi";
import { HelpTip } from "../../components/GeneralComponent/GeneralComponent";
import PermissionWrapper from '../../PermissionWrapper';

export function ManageHeatmaps() {
  useDocumentTitle("Visiopt - Insights");
  const navigate = useNavigate();

  const [selectedMemberWebsite, setSelectedMemberWebsite] = useState('');
  const [heatMapList, setHeatMapList] = useState([]);
  const [sortedHeatmapList, setSortedHeatmapList] = useState([]);
  const [sortKeyName, setSortKeyName] = useState('');
  const [sortingDirection, setSortingDirection] = useState('');
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [HeatmapButtonShow, setHeatmapButtonShow] = useState('none');
  // Which heatmap has a pause/resume/delete/high-accuracy request in flight.
  // Each of those calls regenerates the whole site tracking file, so we allow
  // only one at a time and lock the row's buttons until it finishes — this stops
  // rapid double-clicks firing duplicate regenerations.
  const [processingHeatmapId, setProcessingHeatmapId] = useState<any>(null);
  const handleHeaderMenuComponentChange = (Value:any) => {
    setSelectedMemberWebsite(Value);
  };

  const handleRecordToggle = (pauseStatus:any, heatmapId:any, heatmapPageId:any) => {
    if(pauseStatus === '0') {
      modals.openConfirmModal({
        trapFocus: false,
        title: 'Please confirm your action',
        children: (
          <Text size="sm">
            Are you sure you want to pause this Heat Map?
          </Text>
        ),
        labels: { confirm: 'Confirm', cancel: 'Cancel' },
        onCancel: () => {console.log('Cancel')},
        onConfirm: () => handleUpdateHeatmapRecord(heatmapId, pauseStatus, heatmapPageId)
      });
    }else{
      handleUpdateHeatmapRecord(heatmapId, pauseStatus, heatmapPageId);
    }
  };

  const handleUpdateHeatmapRecord = (heatMapId:any, pauseStatus:any, heatmapPageId:any) => {
    if (processingHeatmapId !== null) return; // a heatmap action is already running
    setProcessingHeatmapId(heatMapId);
    InsightsApi.resumeheatmap({
      heatmap_id: heatMapId,
      pause_status: pauseStatus,
      page_id: heatmapPageId
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            navigate("/logout");
          }
        }
      })
      .then(data => {
        if(data.status === 1){
          fetchHeatMapList();
        }else{
          notifications.show({
            color: 'red',
            icon: <IconX size="1.1rem" />,
            title: 'Error',
            message: data.message,
          });
        }
      })
      .catch(error => {
        console.error("API Error:", error);
      })
      .finally(() => {
        setProcessingHeatmapId(null);
      });
  };

  const updateReport = (heat_map_id:any) => {
    navigate("/create-heatmap", { state: { heat_map_id: heat_map_id} });
  };

  const deleteHeatmapConfirm = (pauseStatus:any, deleteHeatmapId:any, heatmapPageId:any) => modals.openConfirmModal({
    trapFocus: false,
    title: 'Please confirm your action',
    children: (
      <Text size="sm">
        Are you sure you want to delete this Heat Map?
      </Text>
    ),
    labels: { confirm: 'Confirm', cancel: 'Cancel' },
    onCancel: () => {console.log('Cancel')},
    onConfirm: () => handleUpdateHeatmapRecord(deleteHeatmapId, pauseStatus, heatmapPageId)
  });

  const handleHeatMapClick = (heatmap_id:any,heatmapname:any,page_url:any) => {
    navigate(`/heatmap-view/${heatmap_id}`);
  };

  const fetchHeatMapList = () => {
    setShowSkeleton(true);
    InsightsApi.getheatmaplist({
        system_website_id: selectedMemberWebsite
      })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            navigate("/logout");
            setShowSkeleton(false);
          }
        }
      })
      .then(data => {
        if(data.status === 1){
          setShowSkeleton(false);
          setHeatMapList(data.data.heatmap_list);
          setSortedHeatmapList(data.data.heatmap_list);
          if(data.data.heatmap_button_show === 1){
            setHeatmapButtonShow('block');
          }
        }
      })
      .catch(error => {
        setShowSkeleton(false);
        console.error("API Error:", error);
      });
  };

  const handleHighAccuracyChange = (heatMapId:any, highAccuracyValue:any) => {
    if (processingHeatmapId !== null) return; // a heatmap action is already running
    setProcessingHeatmapId(heatMapId);
    const dataObject = {
      heatmap_id: heatMapId,
      high_accuracy: highAccuracyValue !== '' ? '0' : '1',
      system_website_id: selectedMemberWebsite
    };
    InsightsApi.updatehighaccuracy(dataObject)
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            navigate("/logout");
          }
        }
      })
      .then(data => {
        if(data.status === 1){
          fetchHeatMapList();
        }
      })
      .catch(error => {
        console.error("API Error:", error);
      })
      .finally(() => {
        setProcessingHeatmapId(null);
      });
  };

  useEffect(() => {
    if(selectedMemberWebsite !== '') {
      fetchHeatMapList();
    }
  }, [selectedMemberWebsite]);


  const handleSorting = (keyName: string) => {
    let newDirection = sortingDirection === 'desc' ? 'asc' : 'desc';
    setSortingDirection(newDirection);
    setSortKeyName(keyName);
    
    const sortedHeatMapList = [...heatMapList].sort((a, b) => {
      if (!keyName) return 0;

      let valueA = a[keyName];
      let valueB = b[keyName];
      if (keyName === 'create_at') {
        valueA = new Date(a[keyName]);
        valueB = new Date(b[keyName]);
      } else {
        valueA = a[keyName]?.toString().toLowerCase() || '';
        valueB = b[keyName]?.toString().toLowerCase() || '';
      }

      if (valueA < valueB) return newDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return newDirection === 'asc' ? 1 : -1;
      return 0;
    });
    setSortedHeatmapList(sortedHeatMapList);
  };

  const handleKeywordChange = (e) => {
    const keyword = e.target.value;

    const filteredList = heatMapList.filter(data =>
      data.heatmap_name?.toLowerCase().includes(keyword.toLowerCase())
    );

    setSortedHeatmapList(filteredList);
  };

	return (
		<>
      <HeaderMenu HeaderMenuValue={selectedMemberWebsite} HeaderMenuOnChange={handleHeaderMenuComponentChange}/>
      <Container size="xl">
        <Card shadow="sm" padding="sm" radius="md" withBorder>
          <Group position="apart" mb="xs">
            <Text weight={500}>HEAT MAPS <HelpTip helpId={152} /></Text>
          </Group>
          <PermissionWrapper permission="operational_access">
          <Button type="button" style={{display:HeatmapButtonShow}} >
            <Link to="/create-heatmap" style={{color:"white",textDecoration: "none"}}>
              Create a New Heat Map
            </Link>
          </Button>
          </PermissionWrapper>
          {heatMapList.length > 0 && (
          <Box
            p={4}
            pl={20}
            mt={10}
            sx={(theme) => ({
              padding: "10px",
              borderRadius: theme.radius.md,
              fontWeight: "bold",
            })}
          >
          {heatMapList.some(item => Array.isArray(item) ? item.length > 0 : true) && (
            <Grid gutter="xs">
              <Grid.Col span={2}>
                <Text mt={3} weight={500} onClick={() => handleSorting('heatmap_name')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                    {'Heat Map Name'}
                    {sortKeyName === 'heatmap_name' && (
                      sortingDirection === 'asc' ? <IconCaretUpFilled /> : <IconCaretDownFilled />
                    )}
                  </span>
                </Text>
              </Grid.Col>
              <Grid.Col span={2}>
                <Text mt={3} weight={500} onClick={() => handleSorting('funnel_name')}>                  
                  <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                    {'Funnel Name'}
                    {sortKeyName === 'funnel_name' && (
                      sortingDirection === 'asc' ? <IconCaretUpFilled /> : <IconCaretDownFilled />
                    )}
                  </span>
                </Text>
              </Grid.Col>
              <Grid.Col span={2}>
                <Text mt={3} weight={500} onClick={() => handleSorting('page_url')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                    {'Page URL'}
                    {sortKeyName === 'page_url' && (
                      sortingDirection === 'asc' ? <IconCaretUpFilled /> : <IconCaretDownFilled />
                    )}
                  </span>
                </Text>
              </Grid.Col>
              <Grid.Col span={2}>
                <Text mt={3} weight={500} onClick={() => handleSorting('create_at')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                    {'Date Created'}
                    {sortKeyName === 'create_at' && (
                      sortingDirection === 'asc' ? <IconCaretUpFilled /> : <IconCaretDownFilled />
                    )}
                  </span>
                </Text>
              </Grid.Col>
              <Grid.Col span={2} style={{ display: 'flex', gap: '3px' }}>
                <Text mt={3} weight={500} onClick={() => handleSorting('high_accuracy')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                    {'High Accuracy'}
                    {sortKeyName === 'high_accuracy' && (
                      sortingDirection === 'asc' ? <IconCaretUpFilled /> : <IconCaretDownFilled />
                    )}                  
                  </span>
                </Text>
                <HelpTip helpId={329} />
              </Grid.Col>
              <Grid.Col span={2}>
                <Input icon={<IconSearch size={16} />} 
                  placeholder="Search"
                  maxLength={50}
                  autoFocus
                  onChange={handleKeywordChange}
                  style={{ maxWidth: "250px" }}
                />
              </Grid.Col>
            </Grid>
          )}
          </Box>
          )}
          {showSkeleton ? (
            <Flex direction="column" gap={4}>  
              {Array.from({ length: 10 }).map((_, index) => (
                <Skeleton key={index} height={44} width={1246} />
              ))}
            </Flex>
          ) : (
          sortedHeatmapList && sortedHeatmapList.map((heatMap, heatmapIndex) => (
            <Box
              key={heatmapIndex}
              p={4}
              pl={20}
              mt={10}
              sx={(theme) => ({
                backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : '#f8f9fa',
                padding: "10px",
                borderRadius: theme.radius.md,
                border: "0.0625rem solid transparent",
                borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[1] : '#dee2e6'
              })}
            >
              <Grid gutter="xs">
                <Grid.Col span={2}>
                  <Text
                    mt={3}
                    onClick={() => handleHeatMapClick(heatMap.heatmap_id,heatMap.heatmap_name,heatMap.page_url)}
                    style={{ cursor: 'pointer' }}
                  >
                    {heatMap.heatmap_name}
                  </Text>
                </Grid.Col>
                <Grid.Col span={2}>
                  <Text mt={3} >
                    {heatMap.funnel_name}
                  </Text>
                </Grid.Col>
                <Grid.Col span={2}>
                  <Text mt={3} 
                    style={{
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer'
                    }}
                    title={heatMap.page_url}
                  >
                    {heatMap.page_url}
                  </Text>
                </Grid.Col>
                <Grid.Col span={2}>
                  <Text mt={3} >
                    {heatMap.create_at}
                  </Text>
                </Grid.Col>
                <Grid.Col span={2}>
                  <PermissionWrapper permission="operational_access">
                  <Switch
                    checked={heatMap.high_accuracy !== ''}
                    size="md"
                    onLabel="ON"
                    offLabel="OFF"
                    disabled={processingHeatmapId !== null}
                    onChange={() => handleHighAccuracyChange(heatMap.heatmap_id, heatMap.high_accuracy)}
                  />
                  </PermissionWrapper>
                </Grid.Col>
                <Grid.Col span={2}>
                  <Flex
                    justify="flex-end"
                    align="center"
                    direction="row"
                    wrap="wrap"
                    gap={2}
                  >
                  <PermissionWrapper permission="operational_access">
                  <ActionIcon
                    variant="default"
                    size="lg"
                    mx={3}
                    loading={processingHeatmapId === heatMap.heatmap_id}
                    disabled={processingHeatmapId !== null}
                    onClick={() => handleRecordToggle(heatMap.pause_status, heatMap.heatmap_id, heatMap.page_id)}
                    title="Pause/Resume"
                  >
                    {heatMap.pause_status === '1' ? (
                      <IconPlayerPlay size="1.1rem" />
                    ) : (
                      <IconPlayerPause size="1.1rem" />
                    )}
                  </ActionIcon>
                  <ActionIcon
                    variant="default"
                    size="lg"
                    mx={3}
                    disabled={processingHeatmapId !== null}
                    onClick={() => updateReport(heatMap.heatmap_id)}
                    title="Edit"
                  >
                    <IconPencil size="1.1rem" />
                  </ActionIcon>
                  <ActionIcon
                    variant="default"
                    size="lg"
                    mx={3}
                    disabled={processingHeatmapId !== null}
                    onClick={() => deleteHeatmapConfirm(2, heatMap.heatmap_id, heatMap.page_id)}
                    title="Delete"
                  >
                    <IconTrash size="1.1rem" />
                  </ActionIcon>
                  </PermissionWrapper>
                  </Flex>
                </Grid.Col>
              </Grid>
            </Box>
          ))
          )}
        </Card>
      </Container>
		</>
	);
}